import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import esriConfig from "@arcgis/core/config";
import "@esri/calcite-components/components/calcite-notice";
import "@esri/calcite-components/components/calcite-alert";
import "@esri/calcite-components/components/calcite-button";
import texts from "../assets/texts/texts.js";

import "./serverHelth.css";

export type ServerHealthState = "ok" | "slow" | "overloaded" | "down";

type ServerHealthContextValue = {
  state: ServerHealthState;
  message: string | null;
  setOk: () => void;
  setSlow: (message?: string) => void;
  setOverloaded: (message?: string) => void;
  setDown: (message?: string) => void;
  reset: () => void;
};

const ServerHealthContext = createContext<ServerHealthContextValue | null>(
  null,
);

function getStatus(error: unknown): number | undefined {
  const err = error as any;
  return err?.response?.status;
}

function getMessage(error: unknown): string {
  const err = error as any;
  return String(err?.message || "").toLowerCase();
}

function isTimeoutError(error: unknown): boolean {
  const message = getMessage(error);
  return message.includes("timeout") || message.includes("timed out");
}

function isAbortError(error: unknown): boolean {
  const err = error as any;

  return (
    err?.name === "AbortError" ||
    String(err?.message || "")
      .toLowerCase()
      .includes("abort") ||
    String(err?.message || "")
      .toLowerCase()
      .includes("cancel")
  );
}

function classifyError(error: unknown): ServerHealthState | null {
  if (isAbortError(error)) {
    return null;
  }

  const status = getStatus(error);

  if (
    status === 429 ||
    status === 503 ||
    status === 504 ||
    isTimeoutError(error)
  ) {
    return "overloaded";
  }

  if (status === 500 || status === 502) {
    return "down";
  }

  return null;
}

let interceptorInitialized = false;

function initArcgisMonitoring(callbacks: {
  onOk: () => void;
  onSlow: (message?: string) => void;
  onOverloaded: (message?: string) => void;
  onDown: (message?: string) => void;
}) {
  if (interceptorInitialized) return;
  interceptorInitialized = true;

  esriConfig.request.timeout = 15000;

  esriConfig.request.interceptors?.push({
    before(params) {
      (params as any).__requestStartedAt = performance.now();
    },

    after(response) {
      const startedAt = (response?.requestOptions as any)?.__requestStartedAt;

      if (typeof startedAt === "number") {
        const duration = performance.now() - startedAt;

        if (duration > 3000) {
          callbacks.onSlow(texts.errorHandle.slow.alert.message_1);
        } else {
          callbacks.onOk();
        }
      } else {
       
       callbacks.onOk();
      }

      return response;
    },

    error(error) {
      const type = classifyError(error);
    
      if (type === "overloaded") {
        callbacks.onOverloaded(
          texts.errorHandle.down.alert.message_1,
        );
      } else if (type === "down") {
        callbacks.onDown(texts.errorHandle.down.page.title);
      }
    },
  });
}

export function ServerHealthProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [state, setState] = useState<ServerHealthState>("ok");
  const [message, setMessage] = useState<string | null>(null);

  const lastProblemAtRef = useRef<number | null>(null);

  const setOk = useCallback(() => {
    const lastProblemAt = lastProblemAtRef.current;

    if (lastProblemAt && Date.now() - lastProblemAt < 5000) {
      return;
    }

    setState("ok");
    setMessage(null);
  }, []);

  const setSlow = useCallback((msg = texts.errorHandle.slow.alert.message_2) => {
    setState((current) => {
      if (current === "down" || current === "overloaded") return current;
      return "slow";
    });
    setMessage((current) => current ?? msg);
  }, []);

  const setOverloaded = useCallback(
    (
      msg = texts.errorHandle.overloaded.alert.message_1,
    ) => {
      lastProblemAtRef.current = Date.now();
      setState("overloaded");
      setMessage(msg);
    },
    [],
  );

  const setDown = useCallback(
    (
      msg = texts.errorHandle.down.alert.message_1,
    ) => {
      lastProblemAtRef.current = Date.now();
      setState("down");
      setMessage(msg);
    },
    [],
  );

  const reset = useCallback(() => {
    lastProblemAtRef.current = null;
    setState("ok");
    setMessage(null);
  }, []);

  useEffect(() => {
    initArcgisMonitoring({
      onOk: setOk,
      onSlow: setSlow,
      onOverloaded: setOverloaded,
      onDown: setDown,
    });
  }, [setOk, setSlow, setOverloaded, setDown]);

  const value = useMemo<ServerHealthContextValue>(
    () => ({
      state,
      message,
      setOk,
      setSlow,
      setOverloaded,
      setDown,
      reset,
    }),
    [state, message, setOk, setSlow, setOverloaded, setDown, reset],
  );

  return (
    <ServerHealthContext.Provider value={value}>
      {children}
    </ServerHealthContext.Provider>
  );
}

export function useServerHealth() {
  const ctx = useContext(ServerHealthContext);

  if (!ctx) {
    throw new Error("useServerHealth must be used inside ServerHealthProvider");
  }
  return ctx;
}

export function ServerHealthBanner() {
  const { state, message } = useServerHealth();

  if (state === "ok" || state === "down" || !message) {
    return null;
  }


  return (
    <calcite-alert icon="rangefinder" open label="A report alert" kind="warning">
      <div slot="title">
      {message}
      </div>
    </calcite-alert>
  );
}

export function ServerHealthOverlay() {
  const { state, message, reset } = useServerHealth();

  if (state !== "down") {
    return null;
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 3000,
        background: "rgba(255,255,255,0.94)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        style={{
          width: "100%",
          background: "#ffffff",
          border: "1px solid #dddddd",
          borderRadius: 12,
          padding: 24,
          boxShadow: "0 12px 30px rgba(0,0,0,0.08)",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <h2 style={{ marginTop: 0, marginBottom: 12 }}>
          {texts.errorHandle.down.page.title}
        </h2>
        <div className="down_wrapper">
          <calcite-notice
            open
            icon="exclamation-mark-circle"
            kind="danger"
          width="full"
          >
            <div slot="title">
              {" "}
              {texts.errorHandle.down.page.message_1}
            </div>
          </calcite-notice>
        </div>

        <iframe
          style={{
            width: "100%",
            height: "200px",
            border: "none",
          }}
          src="https://webmaps.kambrno.cz/App_Error_Game/"
        ></iframe>

        <div
          style={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <calcite-button   onClick={() => {
              reset();
              window.location.reload();
            }}
            icon-start="refresh">{texts.errorHandle.down.page.button}</calcite-button>
        </div>
      </div>
    </div>
  );
}
